// DOB validation utility
// Takes YYYY-MM-DD, returns { valid: true } | { valid: false; message: string }

type ValidationResult = { valid: true } | { valid: false; message: string };

export function validateDOB(value: string): ValidationResult {
  // Format check
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return { valid: false, message: 'Date must be in a valid format' };
  }

  const [yearStr, monthStr, dayStr] = value.split('-');
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const day = parseInt(dayStr, 10);

  // Month range
  if (month < 1 || month > 12) {
    return { valid: false, message: 'Month must be between 1 and 12' };
  }

  // Day range (leap-year aware)
  const daysInMonth = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (month === 2 && (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0))) {
    daysInMonth[2] = 29;
  }
  if (day < 1 || day > daysInMonth[month]) {
    return { valid: false, message: `Invalid day for ${monthStr}/${yearStr}` };
  }

  // Year range
  const currentYear = new Date().getFullYear();
  if (year < 1900 || year > currentYear) {
    return { valid: false, message: 'Year must be between 1900 and ' + currentYear };
  }

  // Not in the future
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dobDate = new Date(year, month - 1, day);
  if (dobDate > today) {
    return { valid: false, message: 'Date of birth cannot be in the future' };
  }

  // At least 18 years old
  const eighteenYearsAgo = new Date();
  eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18);
  eighteenYearsAgo.setHours(0, 0, 0, 0);
  if (dobDate > eighteenYearsAgo) {
    return { valid: false, message: 'You must be at least 18 years old' };
  }

  return { valid: true };
}
